import { VFXEffectConfig, VFXEffectRegistry } from '../../VFXEffectRegistry';
import { ringBlast, shardBurst, glintScatter, wispRise, groundCloud, starFlash } from './burstBuilders';
import { ABILITY_SCHOOL_COLORS } from '../../../../shared/constants/AbilitySchoolColors';
import type { DissolveProgram } from '../../players/GhostSpriteDissolve';

type Primitive = VFXEffectConfig['primitives'][number];

// =============================================================================
// Death VFX — school-flavored death beats (death-and-kill-beats.md §1)
// =============================================================================
//
// Each school's beat = a GHOST-sprite dissolve program (the body, run by
// GhostSpriteDissolve) + a grammar-composed particle flourish (this file,
// registered as `death_<school>`). Composed entirely from burstBuilders verbs;
// every def multi-tone + one-shot (lane rules). The soulWisp remnant (§1.3)
// rides inside each def with delayMs≈400 so it peaks after the body read.
//
// killConfirm (§1.2) — the attacker-side glint — is also here as the
// `kill_confirm_<school>` family: ≤300 ms, no shake, ZERO numbers (any
// KILL/+XP text is the reward-text owner's job, one-FCT-owner doctrine).
//
// School fallback everywhere: 'physical' (spec §2.2).

/** The 7 content schools (reaction-matrix census ordering by TOML count). */
export const DEATH_SCHOOLS = ['physical', 'holy', 'nature', 'shadow', 'arcane', 'fire', 'frost'] as const;
export type DeathSchool = (typeof DEATH_SCHOOLS)[number];

const DEFAULT_SCHOOL: DeathSchool = 'physical';

/** Desaturate a school tint toward white — the soulWisp's faded-remnant read. */
function pale(color: number): number {
  const mix = (c: number) => Math.round(c + (0xff - c) * 0.6);
  return (mix((color >> 16) & 0xff) << 16) | (mix((color >> 8) & 0xff) << 8) | mix(color & 0xff);
}

/**
 * soulWisp (§1.3) — the lingering remnant mote rising off the corpse. One
 * one-shot wispRise, delayed past the body-read peak; no persistent aura, no
 * cleanup ownership (tween ends, primitive dies). Skipped entirely for
 * training dummies (instant respawn would overlap it).
 */
export function soulWisp(school?: DeathSchool): Primitive {
  const tint = school ? pale(ABILITY_SCHOOL_COLORS[school] ?? 0xddeeff) : 0xddeeff;
  const prim = wispRise({
    count: 3, spread: 8, radius: 3, color: tint, alpha: 0.7,
    direction: 'up', travel: 55, durationMs: 2400, delayMs: 400,
    glow: true, depth: 96,
  });
  // Marker for the TOML `soul_wisp = false` override (§3.2): lets
  // resolveDeathEffectId() strip exactly this primitive from a def without
  // duplicating the school flourishes.
  (prim as { soulWispMarker?: boolean }).soulWispMarker = true;
  return prim;
}

// ── Ghost dissolve programs (§1.1 school choreography table) ────────────────

const DISSOLVE_PROGRAMS: Record<DeathSchool, DissolveProgram> = {
  // Ember burn-away: orange→charcoal ramp while the body sags + bleeds out.
  fire: { durationMs: 550, tintFrom: 0xff7722, tintTo: 0x221100, scaleYTo: 0.85 },
  // Freeze-shatter: snap to ice-blue, HOLD frozen 140 ms, then pop out ≤120 ms.
  frost: { durationMs: 260, holdMs: 140, tintFrom: 0x99ddff },
  // Wisp-drain: the body contracts toward its center as it drains away.
  shadow: { durationMs: 600, scaleXTo: 0.15, scaleYTo: 0.15 },
  // Light-lift: the body rises and warms as it fades.
  holy: { durationMs: 650, dy: -40, tintFrom: 0xffffff, tintTo: 0xffeecc },
  // Sag + crumble: vertical collapse with a slight bulge, mossing over.
  nature: { durationMs: 550, scaleYTo: 0.5, scaleXTo: 1.1, tintFrom: 0xffffff, tintTo: 0x557733 },
  // Unstable phase-out: alpha flickers out in 3 discrete steps, slight swell.
  arcane: { durationMs: 500, alphaFlickerSteps: 3, scaleXTo: 1.15, scaleYTo: 1.15 },
  // Classic fall+fade: rotate 90° away from facing, drop, longest read.
  physical: { durationMs: 700, rotateDeg: 90, dy: 10 },
};

/** Despawn/training fade: plain alpha-out ghost, no flavor, no flourish. */
export const DESPAWN_FADE_PROGRAM: DissolveProgram = { durationMs: 450 };

// ── Particle flourish defs (grammar-composed, one per school) ───────────────

const DEATH_FIRE: VFXEffectConfig = {
  id: 'death_fire',
  name: 'Death: Fire (ember burn-away)',
  category: 'burst',
  description: 'The body chars away — embers rise off it into a soot haze',
  primitives: [
    wispRise({ count: 6, spread: 14, radius: 3, color: 0xff9933, travel: 44, durationMs: 620, glow: true }),
    wispRise({ count: 4, spread: 10, radius: 2, color: 0xffdd66, travel: 34, durationMs: 520, delayMs: 80, glow: true }),
    groundCloud({ width: 40, height: 12, color: 0x332211, alpha: 0.4, durationMs: 600 }),
    soulWisp('fire'),
  ],
};

const DEATH_FROST: VFXEffectConfig = {
  id: 'death_frost',
  name: 'Death: Frost (freeze-shatter)',
  category: 'burst',
  description: 'The body freezes solid, then shatters into shards',
  // react_shatter vocabulary (same palette/shard shapes), bigger counts;
  // shards delayed 140 ms to sync with the ghost program's hold→pop.
  primitives: [
    shardBurst({ count: 10, spread: 22, color: 0xbbe8ff, shard: { points: [0, -8, 3, 0, -3, 0] }, stroke: { color: 0xffffff, width: 1 }, travel: 60, durationMs: 400, delayMs: 140, glow: true }),
    shardBurst({ count: 6, spread: 14, color: 0xccf0ff, shard: { width: 5, height: 2 }, travel: 44, durationMs: 320, delayMs: 180 }),
    ringBlast({ radius: 14, color: 0xe8f8ff, alpha: 0.5, expandTo: 2.6, durationMs: 280, delayMs: 140, stroke: { color: 0xffffff, width: 2 } }),
    glintScatter({ count: 5, spread: 18, color: 0xffffff, durationMs: 240, delayMs: 200 }),
    soulWisp('frost'),
  ],
};

const DEATH_SHADOW: VFXEffectConfig = {
  id: 'death_shadow',
  name: 'Death: Shadow (wisp-drain)',
  category: 'burst',
  description: 'The body drains inward — dark motes rise off the collapse',
  primitives: [
    wispRise({ count: 6, spread: 14, radius: 3, color: 0x442266, travel: 40, durationMs: 650, glow: true }),
    wispRise({ count: 4, spread: 10, radius: 2.5, color: 0x8844aa, travel: 50, durationMs: 700, delayMs: 90, glow: true }),
    groundCloud({ width: 42, height: 12, color: 0x0a0512, alpha: 0.5, durationMs: 550 }),
    soulWisp('shadow'),
  ],
};

const DEATH_HOLY: VFXEffectConfig = {
  id: 'death_holy',
  name: 'Death: Holy (light-lift)',
  category: 'burst',
  description: 'The body lifts away in light — white and gold glints at liftoff',
  primitives: [
    glintScatter({ count: 6, spread: 18, color: 0xffffff, durationMs: 260 }),
    glintScatter({ count: 5, spread: 12, color: 0xffdd44, radius: 2, durationMs: 300, delayMs: 60 }),
    starFlash({ innerRadius: 4, outerRadius: 10, color: 0xffdd44, alpha: 0.85, expandTo: 1.7, durationMs: 320, delayMs: 120, glow: true }),
    soulWisp('holy'),
  ],
};

const DEATH_NATURE: VFXEffectConfig = {
  id: 'death_nature',
  name: 'Death: Nature (sag + crumble)',
  category: 'burst',
  description: 'The body crumbles into moss — leaf-green motes drift up',
  primitives: [
    wispRise({ count: 6, spread: 16, radius: 3, color: 0x66bb44, travel: 38, durationMs: 600 }),
    wispRise({ count: 4, spread: 12, radius: 2, color: 0x99dd66, travel: 30, durationMs: 520, delayMs: 80, glow: true }),
    groundCloud({ width: 44, height: 13, color: 0x557733, alpha: 0.45, durationMs: 620 }),
    soulWisp('nature'),
  ],
};

const DEATH_ARCANE: VFXEffectConfig = {
  id: 'death_arcane',
  name: 'Death: Arcane (unstable phase-out)',
  category: 'burst',
  description: 'The body flickers out of phase — a violet star pop and glints',
  primitives: [
    starFlash({ innerRadius: 5, outerRadius: 12, color: 0xcc66ff, alpha: 0.9, expandTo: 1.9, durationMs: 300, glow: true }),
    glintScatter({ count: 6, spread: 20, color: 0xff66dd, durationMs: 260, delayMs: 40 }),
    glintScatter({ count: 4, spread: 14, color: 0x9944cc, radius: 2, durationMs: 240, delayMs: 100 }),
    soulWisp('arcane'),
  ],
};

const DEATH_PHYSICAL: VFXEffectConfig = {
  id: 'death_physical',
  name: 'Death: Physical (fall + fade)',
  category: 'burst',
  description: 'The body falls and fades — a quiet puff of dust, deliberately plain',
  primitives: [
    glintScatter({ count: 4, spread: 14, color: 0xaa9988, radius: 2, durationMs: 220 }),
    groundCloud({ width: 36, height: 10, color: 0x665544, alpha: 0.35, durationMs: 500 }),
    soulWisp('physical'),
  ],
};

// ── killConfirm (§1.2) — attacker-side glint family, generated per school ───

/** 3-4 school-tinted glints + a subtle star at the attacker, ≤300 ms.
 *  No screenShake, no floatingText — kill credit is a glint, never a number. */
function killConfirmDef(school: DeathSchool): VFXEffectConfig {
  const tint = ABILITY_SCHOOL_COLORS[school] ?? 0xffffff;
  return {
    id: `kill_confirm_${school}`,
    name: `Kill Confirm (${school})`,
    category: 'burst',
    description: 'Attacker-side kill-credit glint — personal, subtle, numberless',
    primitives: [
      glintScatter({ count: 4, spread: 14, color: tint, durationMs: 260 }),
      glintScatter({ count: 3, spread: 8, color: 0xffffff, radius: 2, durationMs: 200, delayMs: 40 }),
      starFlash({ innerRadius: 3, outerRadius: 8, color: tint, alpha: 0.8, expandTo: 1.5, durationMs: 240, glow: true }),
    ],
  };
}

export const KILL_CONFIRM_VFX: VFXEffectConfig[] = DEATH_SCHOOLS.map(killConfirmDef);

export const DEATH_VFX: VFXEffectConfig[] = [
  DEATH_FIRE,
  DEATH_FROST,
  DEATH_SHADOW,
  DEATH_HOLY,
  DEATH_NATURE,
  DEATH_ARCANE,
  DEATH_PHYSICAL,
  ...KILL_CONFIRM_VFX,
];

// ── School → style derivation (§3.1) — the pure map, drift-guard family ─────

export interface DeathStyle {
  school: DeathSchool;
  effectId: string;
  program: DissolveProgram;
}

export const DEATH_STYLE_BY_SCHOOL: Record<DeathSchool, DeathStyle> = Object.fromEntries(
  DEATH_SCHOOLS.map((school) => [school, { school, effectId: `death_${school}`, program: DISSOLVE_PROGRAMS[school] }]),
) as Record<DeathSchool, DeathStyle>;

/** Killing-blow school → beat style. Undefined/unknown ⇒ physical (§2.2). */
export function resolveDeathStyle(school?: string): DeathStyle {
  return DEATH_STYLE_BY_SCHOOL[(school as DeathSchool) ?? DEFAULT_SCHOOL] ?? DEATH_STYLE_BY_SCHOOL[DEFAULT_SCHOOL];
}

/** killConfirm effect id for the killing-blow school (same fallback rule). */
export function resolveKillConfirmId(school?: string): string {
  return `kill_confirm_${resolveDeathStyle(school).school}`;
}

/**
 * Death-beat effect id honoring the TOML `soul_wisp` override (§3.2).
 * `soulWisp !== false` ⇒ the canonical `death_<school>` def unchanged.
 * `false` ⇒ a lazily registered `death_<school>_nowisp` variant — the same
 * def with the soulWisp primitive stripped (marker-filtered, never
 * hand-copied). Registration is idempotent via the `has()` guard.
 */
export function resolveDeathEffectId(style: DeathStyle, soulWisp: boolean = true): string {
  if (soulWisp) return style.effectId;
  const id = `${style.effectId}_nowisp`;
  if (!VFXEffectRegistry.has(id)) {
    const base = DEATH_VFX.find((d) => d.id === style.effectId);
    if (!base) return style.effectId;
    VFXEffectRegistry.register({
      ...base,
      id,
      name: `${base.name} (no soulWisp)`,
      primitives: base.primitives.filter(
        (p) => !(p as { soulWispMarker?: boolean }).soulWispMarker,
      ),
    });
  }
  return id;
}

// ── Registration ────────────────────────────────────────────────────────────
//
// LANE NOTE (conductor): the aggregator wire (`...DEATH_VFX` in
// VFXAbilityDefs.ts) is conductor-owned (lane rule 4 — shared index). Until it
// lands, the KillBeatDirector self-registers these defs on construction so the
// beat works end-to-end; registration is idempotent (registry overwrites with
// a debug log), so the aggregator wire is safe to add on top.

let registered = false;
export function ensureDeathVfxRegistered(): void {
  if (registered) return;
  registered = true;
  VFXEffectRegistry.registerAll(DEATH_VFX);
}
